import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Trash2, ImageIcon } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { TextField } from "../components/Field";
import { Button } from "../components/ui/button";

const FIELDS = [
  ["ragione_sociale", "Ragione sociale", "sm:col-span-2"],
  ["indirizzo", "Indirizzo (via e numero civico)", "sm:col-span-2"],
  ["cap", "CAP"], ["citta", "Città"], ["provincia", "Provincia (sigla)"],
  ["partita_iva", "Partita IVA"], ["codice_fiscale", "Codice fiscale"],
  ["telefono", "Telefono"], ["email", "Email"], ["pec", "PEC"], ["sito", "Sito web"],
];

const Section = ({ title, children }) => (
  <section className="rise rounded-md border bg-white p-6">
    <h2 className="mb-5 font-display text-2xl font-semibold">{title}</h2>
    <div className="grid gap-4 sm:grid-cols-2">{children}</div>
  </section>
);

const uploadLabel = (busy, logo) => {
  if (busy) return "Caricamento…";
  return logo ? "Sostituisci" : "Carica logo";
};

const LogoSection = ({ logo, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/impostazioni/logo", fd); onChange(data.logo); toast.success("Logo caricato"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); input.current.value = ""; }
  };
  const remove = async () => {
    try { await api.delete("/impostazioni/logo"); onChange(""); toast.success("Logo rimosso"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="rise rounded-md border bg-white p-6" data-testid="logo-section">
      <h2 className="mb-1 font-display text-2xl font-semibold">Logo</h2>
      <p className="mb-5 text-sm text-muted-foreground">Appare nell'intestazione del contratto PDF. PNG con sfondo trasparente consigliato, max 5 MB.</p>
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex h-28 w-56 items-center justify-center rounded-md border border-dashed bg-[#F4EDE1]/40 p-3">
          {logo ? <img src={logo} alt="Logo concessionaria" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
            : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
        </div>
        <div className="flex gap-2">
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="logo-file-input" />
          <Button variant="outline" onClick={() => input.current.click()} disabled={busy} data-testid="logo-upload-btn"><Upload className="mr-1 h-4 w-4" /> {uploadLabel(busy, logo)}</Button>
          {logo && <Button variant="ghost" onClick={remove} data-testid="logo-delete-btn"><Trash2 className="mr-1 h-4 w-4 text-red-600" /> Rimuovi</Button>}
        </div>
      </div>
    </section>
  );
};

export default function Impostazioni() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/impostazioni").then((r) => setF(r.data)); }, []);
  if (!f) return <div className="text-sm text-muted-foreground">Caricamento…</div>;

  const field = ([k, label, cls]) => (
    <TextField key={k} label={label} value={f[k]} onChange={(v) => setF((p) => ({ ...p, [k]: v }))} testId={`impostazioni-${k}-input`} className={cls} />
  );
  const save = async () => {
    setSaving(true);
    try { const { logo, ...rest } = f; const { data } = await api.put("/impostazioni", rest); setF({ ...data, logo }); toast.success("Dati concessionaria salvati"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="Dati concessionaria" subtitle="Questi dati compaiono su tutti i contratti di garanzia PDF (intestazione, parti e piè di pagina)">
        <Button onClick={save} disabled={saving} className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="impostazioni-save-btn">
          <Save className="mr-1 h-4 w-4" /> {saving ? "Salvataggio…" : "Salva"}
        </Button>
      </PageHeader>
      <div className="space-y-6">
        <LogoSection logo={f.logo} onChange={(logo) => setF((p) => ({ ...p, logo }))} />
        <Section title="Sede e dati fiscali">{FIELDS.slice(0, 7).map(field)}</Section>
        <Section title="Contatti">{FIELDS.slice(7).map(field)}</Section>
        <p className="text-xs text-muted-foreground">I campi lasciati vuoti appaiono nel contratto come segnaposto tra parentesi quadre.</p>
      </div>
    </div>
  );
}
